import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { DAYJS_FORMAT } from 'lib/constants';
import { RequestState } from 'modules/types/common';
import { switchAccountSuccess } from 'store/account/actions';
import { RootState } from 'store/types';
import * as actionConstants from './actions.constants';
import { TestResultsFilter, TestResultsListSlice, TestResultsListState } from './types';

export const initState = (): TestResultsListState => ({
  isFetching: false,
  requestState: undefined,
  error: null,
  sortBy: 'desc',
  filters: [],
  filterOptions: [],
  year: '',
  startDate: '',
  endDate: '',
  entities: {},
  ids: []
});

export const testResultsListAdapter = createEntityAdapter<TestResultsListSlice>({
  selectId: testResultsList => testResultsList.consumerId
});

export const testResultsListSlice = createSlice({
  name: 'testResults',
  initialState: testResultsListAdapter.getInitialState(initState()),
  reducers: {
    testResultsListAdded: (state, action) => {
      state.isFetching = false;
      state.requestState = action.payload.requestState;
      if (action.payload.cacheUpdatedAt) state.lastUpdated = action.payload.cacheUpdatedAt;
      testResultsListAdapter.removeOne(state, action.payload.consumerId);
      testResultsListAdapter.addOne(state, action);
    },
    testResultsListCacheAdded: (state, action) => {
      if (action.payload.cacheUpdatedAt) state.lastUpdated = action.payload.cacheUpdatedAt;
      testResultsListAdapter.removeOne(state, action.payload.consumerId);
      testResultsListAdapter.addOne(state, action);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(switchAccountSuccess.type, state => {
        state.isFetching = false;
        state.requestState = RequestState.INITIALIZED;
        state.error = null;
        state.sortBy = 'desc';
        state.filters = [];
        state.filterOptions = [];
        state.year = '';
        state.startDate = '';
        state.endDate = '';
      })
      .addCase(actionConstants.GET_TEST_RESULTS_LIST, state => {
        state.isFetching = true;
        state.error = null;
        state.requestState = RequestState.REQUESTING;
      })
      .addCase(actionConstants.GET_TEST_RESULTS_LIST_FAIL, (state, action) => {
        state.isFetching = false;
        state.error = action.error;
        state.requestState = RequestState.ERROR;
      })
      .addCase(actionConstants.UPDATE_SORT, (state, action) => {
        state.sortBy = action.payload.sortBy;
      })
      .addCase(actionConstants.UPDATE_FILTER_OPTIONS, (state, action) => {
        state.filterOptions = action.payload.filterOptions;
      })
      .addCase(actionConstants.APPLY_TEST_RESULTS_FILTERS, state => {
        state.filters = state.filterOptions?.filter(
          (filterOption: TestResultsFilter) => filterOption.checked
        );
      })
      .addCase(actionConstants.RESET_TEST_RESULTS_FILTERS, state => {
        state.filters = [];
        state.filterOptions = state.filterOptions?.map((filterOption: TestResultsFilter) => {
          return {
            ...filterOption,
            checked: false
          };
        });
        state.year = '';
        state.startDate = dayjs().format(DAYJS_FORMAT);
        state.endDate = dayjs().format(DAYJS_FORMAT);
      })
      .addCase(actionConstants.UPDATE_DATE_RANGE, (state, action) => {
        state.year = action.payload.year;
        state.startDate = action.payload.startDate;
        state.endDate = action.payload.endDate;
      });
  }
});

export const testResultsListDataSelectors = testResultsListAdapter.getSelectors<RootState>(
  state => state.testResults.testResultsList
);
